import dynamic from 'next/dynamic';
import { Tooltip } from 'reactstrap';
import { BANNER_TYPE } from '@/types';

import Banner from '@/components/common/Banner';
import Divider from '@/components/common/Divider';
import Icon from '@/components/common/iconedLinks/Icon';
import CheckCgv from '@/components/checkout/common/Summary/CheckCgv';
import MillesimaPrice from '@/components/common/price/MillesimaPrice';
import PriceLine from '@/components/customization/controls/PriceLine';
import { ApplePayButton } from '@/components/checkout/section/ApplePayButton';
import CheckBillAddr from '@/components/checkout/common/Summary/CheckBillAddr';
import PromoProgress from '@/components/checkout/common/Summary/PromoProgress.ds';
import { DiscountForm } from '@/components/checkout/section/sidebar/DiscountForm';
import { useSidebarPresenter } from '@/components/checkout/section/sidebar/presenters';
import CollapsableDetails from '@/components/checkout/common/Summary/CollapsableDetails';
import CallToActionContainer from '@/components/common/callToAction/CallToActionContainer';
const OverrideShippingPrice = dynamic(() => import('@/components/checkout/common/Summary/OverrideShippingPrice'), { ssr: false });
OverrideShippingPrice.displayName = 'OverrideShippingPrice';

import styles from './CheckoutSidebarSection.module.scss';
import { URLS } from '@/constants/urls';

type CheckoutSidebarSectionProps = {
  sidebarPresenter: ReturnType<typeof useSidebarPresenter>
};

export const CheckoutSidebarSection = ({ sidebarPresenter }: CheckoutSidebarSectionProps) => {
  return (
    <aside className={styles.container}>
      {sidebarPresenter.discountForm.shouldShow && (
        <DiscountForm
          t={sidebarPresenter.translate}
          initialCode={sidebarPresenter.discountForm.codeName}
          isReadonly={sidebarPresenter.discountForm.isReadonly}
        />
      )}
      {sidebarPresenter.promotionIncentive.shouldShow && (
        <div className={styles['promotion-incentive']}>
          {sidebarPresenter.promotionIncentive.promos.map(({ text, url, percentage }, i) => (
            <PromoProgress key={i} text={text} url={url} percentage={percentage} />
          ))}
        </div>
      )}
      <div className={styles['second-content']}>
        <h2>{sidebarPresenter.translate('checkout:cart.summary')}</h2>
        <div className={styles['prices-container']}>
          {sidebarPresenter.pricesSection.subTotal.shouldShow && (
            <PriceLine
              className={styles['sub-total']}
              name={sidebarPresenter.pricesSection.subTotal.label}
              price={sidebarPresenter.pricesSection.subTotal.value}
              data-test-id="subTotal-text"
              data-rbf="subTotal-text"
              id="subTotal-text"
              sensefuel-id="subTotal-text"
              addTitle
            />
          )}
          {sidebarPresenter.priceOverriding.shouldShow && (
            <OverrideShippingPrice />
          )}
          {sidebarPresenter.pricesSection.shippingCost.shouldShow && (
            <div className={styles.costs}>
              {sidebarPresenter.pricesSection.shippingCost.label}
              <MillesimaPrice
                value={sidebarPresenter.pricesSection.shippingCost.value}
                className={{ value: sidebarPresenter.pricesSection.shippingCostIsFree ? styles['free-shipping'] : '' }}
              />
            </div>
          )}
          {sidebarPresenter.pricesSection.additionalFees.shouldShow && (
            <div className={styles.costs}>
              {sidebarPresenter.pricesSection.additionalFees.label}
              <MillesimaPrice
                value={sidebarPresenter.pricesSection.additionalFees.value}
              />
            </div>
          )}
          {sidebarPresenter.pricesSection.totalTva.shouldShow && (
            <PriceLine
              addTitle className={styles['sub-total']}
              price={sidebarPresenter.pricesSection.totalTva.value}
              name={sidebarPresenter.pricesSection.totalTva.label}
            />
          )}
          {sidebarPresenter.pricesSection.totalDiscountOrder.shouldShow && (
            <div className={styles.costs}>
              <section className={styles.discount}>
                {sidebarPresenter.pricesSection.totalDiscountOrder.label}
                <Icon src={URLS.ICON_QUESTION_HELP} alt="question" id="promotionTooltip" />
                <Tooltip
                  placement="top" isOpen={sidebarPresenter.pricesSection.totalDiscountOrder.descriptionShown}
                  target="promotionTooltip" toggle={sidebarPresenter.pricesSection.totalDiscountOrder.showOrHideDescription}>
                  <span dangerouslySetInnerHTML={{ __html: sidebarPresenter.pricesSection.totalDiscountOrder.description }} />
                </Tooltip>
              </section>
              <MillesimaPrice value={`-${sidebarPresenter.pricesSection.totalDiscountOrder.value}`} />
            </div>
          )}
          <Divider/>
          {sidebarPresenter.pricesSection.totalTTC.shouldShow && (
            <div className={styles.total}>
              {sidebarPresenter.pricesSection.totalTTC.label}
              <MillesimaPrice value={sidebarPresenter.pricesSection.totalTTC.value} />
            </div>
          )}
        </div>
        {sidebarPresenter.clientIsEligibleForTaxFreeBilling.shouldShow && (
          sidebarPresenter.clientIsEligibleForTaxFreeBilling.label
        )}
        {sidebarPresenter.toggleShippingAsBilling.shouldShow && (
          <CheckBillAddr
            t={sidebarPresenter.translate}
            useAsBilling={sidebarPresenter.toggleShippingAsBilling.isSameAddress}
            disabled={sidebarPresenter.toggleShippingAsBilling.shouldDisable}
            onChange={sidebarPresenter.toggleShippingAsBilling.onCheckOrUncheck}
          />
        )}
        {sidebarPresenter.confirmLegalConditions.shouldShow && sidebarPresenter.isDesktop && (
          <CheckCgv
            required
            t={sidebarPresenter.translate}
            acceptCgv={sidebarPresenter.confirmLegalConditions.state}
            onChange={sidebarPresenter.confirmLegalConditions.change}
          />
        )}
        {sidebarPresenter.applePayButton.shouldShow && (
          <ApplePayButton />
        )}
        {sidebarPresenter.isDesktop && (
          <CallToActionContainer {...sidebarPresenter.continueButton.props}>
            {sidebarPresenter.continueButton.label}
          </CallToActionContainer>
        )}
        {sidebarPresenter.clientIsEligibleForTaxFreeBilling.shouldShow &&
          <Banner type={BANNER_TYPE.INFORMATION} content={sidebarPresenter.clientIsEligibleForTaxFreeBilling.info} />
        }

        {sidebarPresenter.collapsableDetails.shouldShow && (
          <div className={styles['collapsable-details']}>
            <CollapsableDetails
              label={sidebarPresenter.translate('account.orders.details.payment')}
              details={[
                {
                  label: sidebarPresenter.collapsableDetails.totalHT.label,
                  value: <MillesimaPrice value={sidebarPresenter.collapsableDetails.totalHT.value} />
                },
                {
                  label: sidebarPresenter.collapsableDetails.totalTTC.label,
                  value: <MillesimaPrice value={sidebarPresenter.collapsableDetails.totalTTC.value} />
                }
              ]}
            />
          </div>
        )}
      </div>
      {sidebarPresenter.legalConditions.cgv.shouldShow && (
        <p className={styles.cgv}>{sidebarPresenter.legalConditions.cgv.label}&nbsp;
          <a
            aria-label={sidebarPresenter.legalConditions.cgu.label}
            className={styles.link} href={sidebarPresenter.legalConditions.cgv.href}
          >
            {sidebarPresenter.legalConditions.cgv.linkLabel}
          </a>
        </p>
      )}
      {sidebarPresenter.legalConditions.cgu.shouldShow && (
        <p className={styles.cgv}>{sidebarPresenter.legalConditions.cgu.label}&nbsp;
          <a
            className={styles.link} href={sidebarPresenter.legalConditions.cgu.href}
            target="_blank" rel="noopener, noreferrer" aria-label={sidebarPresenter.legalConditions.cgu.linkLabel}
          >
            {sidebarPresenter.legalConditions.cgu.linkLabel}
          </a>
        </p>
      )}
    </aside>
  );
};
